import { Col, Button, Accordion } from 'react-bootstrap';
import { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import styles from './Pathway.module.css';
import { ProfileContext } from "pages/Profile";
import StandardModal from 'components/general/defaultModals/StandardModal';

const normalizeText = (value) => String(value || '').trim();
const normalizeLower = (value) => String(value || '').trim().toLowerCase();

const normalizeId = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    if (value._id !== undefined && value._id !== null) return String(value._id);
    if (value.id !== undefined && value.id !== null) return String(value.id);
  }
  return String(value);
};

const getPathwayProgress = (pathway, userProjects) => {
  const projects = Array.isArray(userProjects) ? userProjects : [];
  const completedIds = new Set(
    projects
      .filter((project) => Boolean(project?.completed || project?.completedAt))
      .map((project) => normalizeId(project?.projectId))
      .filter(Boolean)
  );

  let totalProjects = 0;
  let selectedProjects = projects.length;
  let requiredProjects = 0;
  let completedRequired = 0;

  [1, 2, 3, 4, 5].forEach((levelNo) => {
    const level = pathway?.[`level${levelNo}`];
    if (!level) return;

    const mandatory = Array.isArray(level?.mandatoryProjects) ? level.mandatoryProjects : [];
    const elective = Array.isArray(level?.electiveProjects) ? level.electiveProjects : [];
    const minElectives = Number(level?.minElectives || 0);
    const electiveGoal = minElectives > 0 ? minElectives : elective.length;

    totalProjects += mandatory.length + elective.length;
    requiredProjects += mandatory.length + electiveGoal;

    const mandatoryCompleted = mandatory
      .filter((project) => completedIds.has(normalizeId(project?._id))).length;
    const electiveCompleted = elective
      .filter((project) => completedIds.has(normalizeId(project?._id))).length;

    completedRequired += mandatoryCompleted + Math.min(electiveCompleted, electiveGoal);
  });

  selectedProjects = Math.min(selectedProjects, totalProjects);
  const percent = requiredProjects > 0
    ? Math.round((Math.min(completedRequired, requiredProjects) / requiredProjects) * 100)
    : 0;

  return {
    totalProjects,
    selectedProjects,
    requiredProjects,
    completedRequired,
    percent,
  };
};

const getShortProjectDescription = (project) => {
  const raw = normalizeText(project?.description1 || project?.description2 || '');
  if (!raw) return 'No short description available.';

  const firstSentence = raw.split(/\.\s+/)[0]?.trim() || raw;
  if (firstSentence.length <= 120) return firstSentence;
  return `${firstSentence.slice(0, 117)}...`;
};

const getTimingItems = (project) => ([
  { key: 'green', value: normalizeText(project?.green) },
  { key: 'amber', value: normalizeText(project?.amber) },
  { key: 'red', value: normalizeText(project?.red) },
]).filter((item) => item.value);


export default function Pathway({ pathway, onReload, assignedPathways = null }) {
  const { user: authUser } = useSelector(x => x.auth);
  const { profile } = useContext(ProfileContext);

  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmed, setConfirmed] = useState(null); // null | true | false
  const [openElectives, setOpenElectives] = useState({});
  const [loading, setLoading] = useState(false);
  const [userPathways, setUserPathways] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [reloading, setReloading] = useState(false);
  // const [reloadPathways, setReloadPathways] = useState(0);

  const url = process.env.REACT_APP_URL;

  // console.log(` Pathway ${pathway.name} - authUser ACL: ${authUser?.user?.acl}, authUser ID: ${authUser?.user?._id}, profile ID: ${profile?._id} `);
  const canAssign =
    authUser?.user?.acl === 'Officer' || authUser?.user?._id === profile?._id;


  const handleOpen = () => setShowModal(true);
  const handleReload = async () => {
    if (!onReload || reloading) return;
    setReloading(true);
    try {
      await onReload();
    } finally {
      setReloading(false);
    }
  };
  const handleClose = () => {
    setShowModal(false);
    setShowConfirm(false);
    setConfirmed(null);
    setLoading(false);
  };

  const toggleElectives = (idx) => {
    setOpenElectives(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  const renderProjectCards = (projects, type) => {
    const safeProjects = (Array.isArray(projects) ? projects : []).filter(
      (proj) => proj && normalizeText(proj.title)
    );

    if (safeProjects.length === 0) {
      return <p className="pathway-projects-empty mb-0">No projects listed.</p>;
    }

    return (
      <div className="pathway-project-cards">
        {safeProjects.map((proj, idx) => {
          const timings = getTimingItems(proj);
          return (
            <article key={proj._id || `${type}-${idx}`} className="pathway-project-card">
              <div className="pathway-project-card-top">
                <h6 className="pathway-project-title">{proj.title}</h6>
                <span className={`pathway-project-type pathway-project-type--${type}`}>
                  {type === 'mandatory' ? 'Mandatory' : 'Elective'}
                </span>
              </div>
              <p className="pathway-project-description mb-2">{getShortProjectDescription(proj)}</p>
              {timings.length > 0 && (
                <div className="pathway-project-timers">
                  {timings.map((timer) => (
                    <span
                      key={`${proj._id || idx}-${timer.key}`}
                      className={`pathway-project-timer pathway-project-timer--${timer.key}`}
                      title={timer.key}
                    >
                      {timer.value}
                    </span>
                  ))}
                </div>
              )}
            </article>
          );
        })}
      </div>
    );
  };

  const renderElectives = (levelIdx, minElectives, electiveProjects) => {
    if (!electiveProjects || electiveProjects.length === 0) return null;
    const hasMinimum = minElectives && Number(minElectives) > 0;
    if (hasMinimum) {
      return (
        <div className="pathway-level-section pathway-level-section--electives">
          <div className="pathway-level-section-head">
            <h6 className="pathway-level-section-title mb-0">Elective Projects</h6>
            <Button
              type="button"
              className="pathway-electives-toggle"
              onClick={() => toggleElectives(levelIdx)}
              aria-expanded={!!openElectives[levelIdx]}
            >
              {openElectives[levelIdx] ? 'Hide' : `Choose ${minElectives}`}
            </Button>
          </div>
          {openElectives[levelIdx] && (
            <>
              <div className="pathway-electives-note">
                Choose {minElectives} of the following:
              </div>
              {renderProjectCards(electiveProjects, 'elective')}
            </>
          )}
        </div>
      );
    }
    return (
      <div className="pathway-level-section pathway-level-section--electives">
        <h6 className="pathway-level-section-title">Elective Projects</h6>
        {renderProjectCards(electiveProjects, 'elective')}
      </div>
    );
  };

  const levels = [1, 2, 3, 4, 5]
    .map(levelNum => {
      const levelKey = `level${levelNum}`;
      const levelData = pathway[levelKey];
      if (!levelData) return null;
      const mandatoryProjects = levelData.mandatoryProjects || [];
      const electiveProjects = levelData.electiveProjects || [];
      // Skip levels with no content at all
      if (mandatoryProjects.length === 0 && electiveProjects.length === 0 && !levelData.title) return null;
      return {
        label: levelData.title || `Level ${levelNum}`,
        mandatoryProjects,
        electiveProjects,
        minElectives: levelData.minElectives
      };
    })
    .filter(Boolean);

  const handleNo = () => {
    setConfirmed(false);
    setTimeout(() => {
      setShowConfirm(false);
      setConfirmed(null);
    }, 10000);
  };

  const handleAssignPathway = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${url}/userpathway`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authUser.token}`
        },
        body: JSON.stringify({
          user: profile._id,
          pathwayId: pathway._id
        })
      });
      if (res.ok) {
        setConfirmed(true);
        setLoading(false);
        // Close modal after a short delay, refresh pathways only
        setTimeout(() => {
          setShowModal(false);
        }, 600);
      } else {
        setConfirmed(false);
        setLoading(false);
      }
    } catch (err) {
      setConfirmed(false);
      setLoading(false);
    }
  };

  const getProfilePathways = useCallback(async () => {
    try {
      const res = await fetch(`${url}/userpathway/${profile._id}`, {
        headers: { 'Authorization': `Bearer ${authUser.token}` }
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        const message = String(data?.error || data?.Error || `Failed to fetch pathways (${res.status})`).trim();
        throw new Error(message);
      }
      const normalizedPathways = Array.isArray(data)
        ? data
        : Array.isArray(data?.userPathways)
          ? data.userPathways
          : Array.isArray(data?.items)
            ? data.items
            : [];
      setUserPathways(normalizedPathways);
    } catch (error) {
      console.error('Error fetching profile pathways:', error);
      setUserPathways([]);
    }
  }, [url, profile?._id, authUser.token]);

  useEffect(() => {
    if (Array.isArray(assignedPathways)) {
      setUserPathways(assignedPathways);
      return;
    }

    if (profile?._id) {
      getProfilePathways();
    }
  }, [assignedPathways, profile?._id, getProfilePathways]);

  const currentPathwayId = normalizeId(pathway?._id);
  const currentPathwayName = normalizeLower(pathway?.name);
  const assignedPathway = (Array.isArray(userPathways) ? userPathways : []).find(
    (up) => {
      const assignedPathwayId = normalizeId(up?.pathwayId) || normalizeId(up?.pathway?._id);
      if (assignedPathwayId && currentPathwayId && assignedPathwayId === currentPathwayId) {
        return true;
      }

      const assignedPathwayName = normalizeLower(up?.pathway?.name);
      return Boolean(assignedPathwayName && currentPathwayName && assignedPathwayName === currentPathwayName);
    }
  );

  useEffect(() => {
    if (Array.isArray(assignedPathway?.projects)) {
      setSelectedProjects(assignedPathway.projects);
      return;
    }

    const userPathwayId = normalizeId(assignedPathway?._id);
    const userId = normalizeId(profile?._id);
    if (!userPathwayId || !userId || !authUser?.token) {
      setSelectedProjects([]);
      return;
    }

    let cancelled = false;

    const fetchSelectedProjects = async () => {
      try {
        const response = await fetch(`${url}/user/${userId}/userPathway/${userPathwayId}/projects`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authUser.token}`,
          },
        });

        if (!response.ok) {
          if (!cancelled) setSelectedProjects([]);
          return;
        }

        const data = await response.json();
        if (!cancelled) {
          setSelectedProjects(Array.isArray(data) ? data : []);
        }
      } catch {
        if (!cancelled) setSelectedProjects([]);
      }
    };

    fetchSelectedProjects();

    return () => {
      cancelled = true;
    };
  }, [assignedPathway?._id, assignedPathway?.projects, authUser?.token, profile?._id, url]);

  const progress = useMemo(
    () => getPathwayProgress(pathway, selectedProjects),
    [pathway, selectedProjects]
  );
  const safePercent = Math.max(0, Math.min(100, Number(progress?.percent) || 0));

  const totalProjectCount = levels.reduce(
    (sum, level) => sum + level.mandatoryProjects.length + level.electiveProjects.length,
    0
  );

  const hasLevelContent = levels.some(
    level => level.mandatoryProjects.length > 0 || level.electiveProjects.length > 0
  );

  return (
    <Col xs={12} lg={4} className='mb-5 text-center'>
      <div style={{ position: 'relative', width: '100%' }}>
        <button
          type="button"
          className="profile-overview-reload-btn pathways-catalog-reload-btn"
          onClick={handleReload}
          disabled={reloading}
          aria-label={`Reload ${pathway?.name || 'pathway'}`}
          title="Reload pathway"
        >
          <i className={`bi bi-arrow-clockwise${reloading ? ' profile-overview-reload-icon--spinning' : ''}`} aria-hidden="true" />
        </button>
        <Button
          variant="link"
          className={`h-100 ${styles.pathwayBtn}`}
          style={{
            width: '100%',
            padding: 0,
            background: 'none',
            border: 'none',
          }}
          onClick={handleOpen}
        >
          <span style={{ position: 'relative', display: 'inline-block' }}>
            {/* Status icon overlays the top-right of the image */}
            {assignedPathway && (
              assignedPathway.completedAt
                ? (
                  <i
                    className="bi bi-patch-check-fill"
                    style={{
                      position: 'absolute',
                      top: '-6px',
                      right: '-6px',
                      fontSize: '1.1rem',
                      color: '#198754',
                      zIndex: 2,
                      pointerEvents: 'none',
                      filter: 'drop-shadow(0 1px 2px #222)'
                    }}
                    title="Pathway completed"
                  />
                ) : (
                  <i
                    className="bi bi-hourglass-top"
                    style={{
                      position: 'absolute',
                      top: '-6px',
                      right: '-6px',
                      fontSize: '1.1rem',
                      color: '#ffc107',
                      zIndex: 2,
                      pointerEvents: 'none',
                      filter: 'drop-shadow(0 1px 2px #222)'
                    }}
                    title="This pathway is already selected"
                  />
                )
            )}
            <img
              src={pathway.logo}
              alt={pathway.name}
              className={`mb-4 ${styles.pathwayImg}`}
              style={{ display: 'block' }}
            />
          </span>
          <div className={styles.pathwayName}>{pathway.name}</div>
          {assignedPathway && (
            <div className={styles.pathwayProgressWrap} aria-label="Selected pathway progress">
              <div className={styles.pathwayProgressMeta}>
                <span>Selected {progress.selectedProjects}/{progress.totalProjects}</span>
                <span>Completed {progress.completedRequired}/{progress.requiredProjects} required</span>
              </div>
              <div className={styles.pathwayProgressTrack}>
                <span className={styles.pathwayProgressFill} style={{ width: `${safePercent}%` }} />
              </div>
            </div>
          )}
        </Button>
      </div>

      <StandardModal
        show={showModal}
        handleClose={handleClose}
        title={pathway.name}
        style={{ maxWidth: '95%', marginTop: '4vh' }}
        ModalBodyComponent={(
          <>
            <div className="pathway-modal-hero mb-4">
              <img src={pathway.logo} alt={pathway.name} className="pathway-modal-logo" />
              <div className="pathway-modal-hero-content">
                <p className="pathway-modal-hero-title mb-2">Pathway Overview</p>
                <div className="pathway-modal-badges">
                  <span className="pathway-modal-badge">{levels.length} levels</span>
                  <span className="pathway-modal-badge">{totalProjectCount} projects</span>
                  {pathway?.type && <span className="pathway-modal-badge pathway-modal-badge--type">{pathway.type}</span>}
                </div>
                <div className="pathway-modal-hero-divider" />
                <h6 className="pathway-description-title mb-2">Pathway Description</h6>
                <p className="pathway-modal-description mb-0">{normalizeText(pathway.description)}</p>
              </div>
            </div>

            {hasLevelContent && (
              <Accordion className="pathway-levels-accordion">
                {levels.map((level, idx) => (
                  <Accordion.Item
                    eventKey={String(idx)}
                    key={`${pathway._id || pathway.name}-level-${idx}`}
                    className="pathway-level-accordion-item"
                  >
                    <Accordion.Header>
                      <div className="pathway-level-accordion-head">
                        <h5 className="mb-1 pathway-level-accordion-title">{level.label}</h5>
                        <div className="pathway-level-counts">
                          <span>{level.mandatoryProjects.length} mandatory</span>
                          <span>
                            {level.minElectives && Number(level.minElectives) > 0
                              ? `${level.minElectives} out of ${level.electiveProjects.length} elective`
                              : `${level.electiveProjects.length} elective`}
                          </span>
                        </div>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                      <section className="pathway-level-card pathway-level-card--inside">
                        {level.mandatoryProjects.length > 0 && (
                          <div className="pathway-level-section">
                            <h6 className="pathway-level-section-title">Mandatory Projects</h6>
                            {renderProjectCards(level.mandatoryProjects, 'mandatory')}
                          </div>
                        )}

                        {renderElectives(idx, level.minElectives, level.electiveProjects)}
                      </section>
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            )}

            {/* Add to my pathways button and confirmation logic */}
            {canAssign && (
              <div className="mt-4 mb-2" style={{ width: '100%' }}>
                {assignedPathway ? (
                  <div style={{
                    background: '#198754',
                    color: '#fff',
                    borderRadius: 8,
                    padding: '1rem',
                    marginTop: '1rem',
                    marginBottom: '1rem',
                    textAlign: 'center'
                  }}>
                    <h5>
                      You have added to your pathways the path <b>{pathway.name}</b>.
                    </h5>
                  </div>
                ) : (
                  <>
                    {!showConfirm && confirmed !== true && (
                      <Button
                        variant="success"
                        size="lg"
                        style={{ width: '100%', fontWeight: 'bold', fontSize: '1.1rem' }}
                        onClick={() => setShowConfirm(true)}
                      >
                        {authUser.user._id === profile._id
                          ? 'Add to my Pathways'
                          : 'Add to User Pathways'}
                      </Button>
                    )}

                    {showConfirm && confirmed !== true && (
                      <div style={{
                        background: '#dc3545',
                        color: '#fff',
                        borderRadius: 8,
                        padding: '1rem',
                        marginTop: '1rem',
                        marginBottom: '1rem',
                        textAlign: 'center'
                      }}>
                        {confirmed === false ? (
                          <h5 style={{ background: '#ffc107', color: '#23272b', borderRadius: 8, padding: '1rem', margin: 0 }}>
                            No worries! Take your time to select your path on the Toastmasters International website first. 🚀
                          </h5>
                        ) : (
                          <>
                            <h5>
                              Are you sure you have already selected this pathway in Toastmasters International website and it is visible in your BaseCamp?
                            </h5>
                            <div className="d-flex justify-content-center gap-3 mt-3">
                              <Button
                                variant="success"
                                size="md"
                                disabled={loading}
                                onClick={handleAssignPathway}
                              >
                                {loading ? 'Adding...' : 'Yes'}
                              </Button>
                              <Button
                                variant="danger"
                                size="md"
                                onClick={handleNo}
                              >
                                No
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {confirmed === true && (
                      <div style={{
                        background: '#198754',
                        color: '#fff',
                        borderRadius: 8,
                        padding: '1rem',
                        marginTop: '1rem',
                        marginBottom: '1rem',
                        textAlign: 'center'
                      }}>
                        <h5>
                          You have added to your pathways the path <b>{pathway.name}</b>.
                        </h5>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}
        ModalFooterComponent={(
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        )}
      />
    </Col>
  );
}